<script setup>
import { ref, computed, watch } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { useDetectDevice } from '@/composables/device'
import {
  DxChart,
  DxSeries,
  DxLegend,
  DxTooltip,
  DxCommonSeriesSettings,
  DxValueAxis,
  DxArgumentAxis,
  DxLabel,
} from 'devextreme-vue/chart'
import { planningApi } from '@/apis/planning.api'
import {
  KPI_CHART_TYPE_ENUM,
  KPI_CHART_PERIOD_ENUM,
  toKpiApiParams,
} from '@/composables/dashboard-kpi'
import { useDashboardStore } from '@/stores/dashboard.store'

const props = defineProps({
  tileId: { type: String, required: true },
  chartConfig: { type: Object, default: () => ({}) },
  tileName: { type: String, default: '' },
})
const emit = defineEmits(['config-change', 'edit'])

const { t } = getI18n()
const { isMobile } = useDetectDevice()
const dashboardStore = useDashboardStore()

const points = ref([])
const loading = ref(true)

const selectedMetric = computed(() => props.chartConfig.metric ?? dashboardStore.kpiChartDefaultByTile[props.tileId] ?? 'new_projects_this_month')
const chartType = computed(() => props.chartConfig.chartType ?? KPI_CHART_TYPE_ENUM.LINE)
const period = computed(() => props.chartConfig.period ?? KPI_CHART_PERIOD_ENUM.THIRTY_DAYS)
const metric = computed(() => dashboardStore.kpiMetric(selectedMetric.value))
const activeFilters = computed(() => ({
  warehouseIds: props.chartConfig.warehouseIds ?? [],
  periodType: props.chartConfig.periodType ?? 'all',
  periodFrom: props.chartConfig.periodFrom ?? null,
  periodTo: props.chartConfig.periodTo ?? null,
}))

const DX_SERIES_TYPE = Object.freeze({
  [KPI_CHART_TYPE_ENUM.LINE]:   'line',
  [KPI_CHART_TYPE_ENUM.SPLINE]: 'spline',
  [KPI_CHART_TYPE_ENUM.BAR]:    'bar',
})

const PERIOD_LABEL_KEY = Object.freeze({
  [KPI_CHART_PERIOD_ENUM.THIRTY_DAYS]: 'dashboard.kpiChart30d',
  [KPI_CHART_PERIOD_ENUM.QUARTERLY]:   'dashboard.kpiChartQuarterly',
  [KPI_CHART_PERIOD_ENUM.YEARLY]:      'dashboard.kpiChartYearly',
})

const requestParams = computed(() => toKpiApiParams(activeFilters.value))
const requestKey = computed(() => JSON.stringify([selectedMetric.value, period.value, requestParams.value]))

let fetchSeq = 0
const fetchData = async () => {
  const seq = ++fetchSeq
  loading.value = true
  const res = await planningApi.getProjectKpiChartAsync(selectedMetric.value, period.value, requestParams.value)
  if (seq === fetchSeq) {
    points.value = res.data.points
    loading.value = false
  }
}

const adjustPeriod = (delta) => {
  const next = period.value + delta
  if (next >= 0 && next <= 2) {
    emit('config-change', { ...props.chartConfig, period: next })
  }
}

// chartConfig is rebuilt on every parent render (grid relayout on window resize): compare by value
watch(requestKey, fetchData, { immediate: true })

const customizeTooltip = ({ argument, value }) => ({
  text: `${argument}: ${metric.value?.format(value) ?? value}`,
})
</script>

<template>
  <div class="kpi-chart-tile fit" style="position: relative;">

    <!-- Period controls + settings gear -->
    <div class="top-controls">
      <q-btn
        flat
        dense
        round
        size="xs"
        icon="mdi-minus"
        color="grey-6"
        :disable="period >= 2"
        @mousedown.stop
        @click.stop="adjustPeriod(1)"
      />
      <span class="period-label text-caption text-grey-6 q-px-xs">{{ t(PERIOD_LABEL_KEY[period]) }}</span>
      <q-btn
        flat
        dense
        round
        size="xs"
        icon="mdi-plus"
        color="grey-6"
        :disable="period <= 0"
        @mousedown.stop
        @click.stop="adjustPeriod(-1)"
      />
      <q-btn
        v-if="isMobile"
        round
        flat
        dense
        size="xs"
        icon="mdi-cog-outline"
        color="grey-5"
        @mousedown.stop
        @click.stop="emit('edit')"
      />
    </div>

    <!-- Tile content -->
    <div class="fit flex flex-center" style="padding: 1.75rem 0.5rem 0.5rem;">
      <div v-if="loading" class="col flex flex-center">
        <q-spinner size="md" color="primary" />
      </div>
      <dx-chart
        v-else
        :data-source="points"
        class="fit"
        style="min-height: 60px;"
      >
        <dx-common-series-settings
          :type="DX_SERIES_TYPE[chartType]"
          argument-field="label"
          value-field="value"
        />
        <dx-series color="#1976D2">
          <dx-label :visible="false" />
        </dx-series>
        <dx-argument-axis>
          <dx-label :overlapping-behavior="'rotate'" :rotation-angle="-35" />
        </dx-argument-axis>
        <dx-value-axis />
        <dx-legend :visible="false" />
        <dx-tooltip :enabled="true" :customize-tooltip="customizeTooltip" />
      </dx-chart>
    </div>

  </div>
</template>

<style scoped>
.top-controls {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s;
}
.kpi-chart-tile:hover .top-controls {
  opacity: 1;
}
.period-label {
  font-size: 0.7rem;
  min-width: 3.5rem;
  text-align: center;
}
</style>
